import React, { useContext, useMemo, useState } from "react";
import { Typography, Button as ButtonBase, Icon } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { toastr } from "react-redux-toastr";
import { useNavigate } from "react-router-dom";

import RpMUIModalBase from "@/components/modals/RpMUIModal";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { GlobalContext } from "@/helpers/contexts/contexts";
import { HTTP_STATUS } from "@/config/constants";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

type PauseLocation = {
  system_generated_location_id?: string | number;
  originalStatus?: number;
  is_pause_campaign?: number;
  [key: string]: unknown;
};

type PauseConfirmationModalProps = {
  open: boolean;
  onClose: () => void;
  locations?: PauseLocation[];
  initialLocations?: PauseLocation[];
  campaignId?: string | number;
  isPMax?: boolean;
};

type PauseContext = {
  selectedBrand?: {
    id?: string | number;
    brand_name?: string;
  } | null;
};

type PauseRequestError = {
  response?: {
    data?: {
      message?: string;
    };
  };
};

type PauseStatusChanges = {
  toPause: number;
  toActivate: number;
};

const RpMUIModal = RpMUIModalBase as React.ComponentType<any>;
const Button = ButtonBase as React.ComponentType<any>;

/**
 * Modal component for confirming location status changes (pause/activate)
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Handler for modal close
 * @param {Array} props.locations - Current locations with updated status
 * @param {Array} props.initialLocations - Initial locations before status changes
 * @param {string} props.campaignId - ID of the campaign
 */
const PauseConfirmationModal = ({
  open,
  onClose,
  locations = [],
  campaignId,
  isPMax,
}: PauseConfirmationModalProps) => {
  const { selectedBrand } = useContext(GlobalContext) as PauseContext;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Calculate status changes by comparing current and initial states
  const statusChanges = useMemo(() => {
    return locations.reduce<PauseStatusChanges>(
      (acc, loc) => {
        if (loc.originalStatus !== loc.is_pause_campaign) {
          loc.is_pause_campaign ? acc.toActivate++ : acc.toPause++;
        }
        return acc;
      },
      { toPause: 0, toActivate: 0 }
    );
  }, [locations]);

  // Generate appropriate message based on status changes
  const getMessage = useMemo(() => {
    const { toPause, toActivate } = statusChanges;

    // Helper for pluralization
    const pluralize = (count: number, word: string) =>
      `${count} ${count === 1 ? word : word + "s"}`;

    // Case 1: Only pausing locations
    if (toPause > 0 && toActivate === 0) {
      return `You're pausing ${isPMax ? `${locations?.length} locations` : pluralize(toPause, "location")}. Keep in mind that when ads stop and re-start, performance may suffer due to a re-ramp period.`;
    }

    // Case 2: Only activating locations
    if (toActivate > 0 && toPause === 0) {
      return `You're setting ${isPMax ? `${locations?.length} locations` : pluralize(toActivate, "location")} live. Keep in mind that when ads stop and re-start, performance may suffer due to a re-ramp period.`;
    }

    // Case 3: Both pausing and activating locations
    return `You're setting ${pluralize(toActivate, "location")} live. You're pausing ${pluralize(toPause, "location")}. Keep in mind that when ads stop and re-start, performance may suffer due to a re-ramp period.`;
  }, [statusChanges]);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      // Get locations with changed status
      const statusWhenPMax = locations?.[0]?.is_pause_campaign;
      const changedLocations = isPMax
        ? locations.map((l) => ({ ...l, is_pause_campaign: statusWhenPMax }))
        : locations.filter(
            (location) => location.originalStatus !== location.is_pause_campaign
          );

      // Prepare data for API
      const requestData = {
        campaign_id: campaignId,
        location_ids: changedLocations.map((loc) => ({
          system_generated_location_id: loc.system_generated_location_id,
          is_pause_campaign: loc.is_pause_campaign,
        })),
        brand_id: selectedBrand?.id || null,
        brand_name: selectedBrand?.brand_name || null,
        isPMax,
      };

      const response = await campaignService.requestPauseCampaign(requestData);

      if (response?.status === HTTP_STATUS.OK) {
        toastr.success("Success", "Location status updated successfully!", {
          id: "campaign_pause_success",
        });

        grantThankYouAccessForFlow({
          queryType: "campaign_pause",
          stateFrom: "campaign_pause",
        });
        navigate(`${ROUTES.THANK_YOU}?type=campaign_pause`, {
          replace: true,
          state: {
            from: "campaign_pause",
          },
        });
      } else {
        throw new Error("Failed to update campaign status");
      }
    } catch (error) {
      const requestError = error as PauseRequestError;
      toastr.error(
        "Error",
        requestError.response?.data?.message ||
          "Failed to update location status"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RpMUIModal open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {isSubmitting && (
        <div className="loading-overlay">
          <CircularProgress color="primary" />
        </div>
      )}
      <div className="status-change-confirmation text-center">
        <Icon
          component="img"
          src="/img/icons/info.svg"
          className="warning-icon mb-2"
        />

        <Typography variant="body1" className="text-center" gutterBottom>
          {getMessage}
        </Typography>

        <Typography variant="h6" className="text-center" gutterBottom>
          Are you sure you want to continue?
        </Typography>
      </div>

      <div className="d-flex justify-content-center gap-3 mt-4">
        <Button
          onClick={onClose}
          color="var(--palette-primary-contrastText)"
          disabled={isSubmitting}
          variant="outlined"
          className="mr-3 px-5"
          size="large"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          className="confirm-btn px-5"
          disabled={isSubmitting}
        >
          Confirm
        </Button>
      </div>
    </RpMUIModal>
  );
};

export default PauseConfirmationModal;
